import React, { useState, useEffect } from 'react';
import { getPodcastEpisodes, getAllFeaturedEpisodes, addFeaturedEpisode, removeFeaturedEpisode, getAllYCEpisodes, addYCEpisode, removeYCEpisode, getAllTopVCEpisodes, addTopVCEpisode, removeTopVCEpisode } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Star, StarOff, Play, Clock, ExternalLink, Loader2 } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import AdminLayout from '../components/AdminLayout';

const AdminPodcast = () => {
  const [episodes, setEpisodes] = useState([]);
  const [featuredEpisodes, setFeaturedEpisodes] = useState([]);
  const [ycEpisodes, setYCEpisodes] = useState([]);
  const [topvcEpisodes, setTopVCEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalEpisodes, setTotalEpisodes] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [podcastData, featured, yc, topvc] = await Promise.all([
        getPodcastEpisodes(50),
        getAllFeaturedEpisodes(),
        getAllYCEpisodes(),
        getAllTopVCEpisodes()
      ]);
      setEpisodes(podcastData.episodes || []);
      setTotalEpisodes(podcastData.total || podcastData.episodes?.length || 0);
      setFeaturedEpisodes(featured || []);
      setYCEpisodes(yc || []);
      setTopVCEpisodes(topvc || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllEpisodes = async () => {
    setLoadingMore(true);
    try {
      const podcastData = await getPodcastEpisodes(500);
      setEpisodes(podcastData.episodes || []);
      setTotalEpisodes(podcastData.total || podcastData.episodes?.length || 0);
    } catch (error) {
      console.error('Error loading all episodes:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleFeature = async (episode) => {
    try {
      const newFeatured = await addFeaturedEpisode({
        episode_guid: episode.guid,
        title: episode.title,
        order: featuredEpisodes.length,
        active: true
      });
      // Update state directly - preserves scroll position
      setFeaturedEpisodes(prev => [...prev, newFeatured]);
    } catch (error) {
      console.error('Error featuring episode:', error);
      alert('Error featuring episode');
    }
  };

  // Refresh only the tag data (featured, YC, TopVC) without reloading episodes
  const refreshTags = async () => {
    try {
      const [featured, yc, topvc] = await Promise.all([
        getAllFeaturedEpisodes(),
        getAllYCEpisodes(),
        getAllTopVCEpisodes()
      ]);
      setFeaturedEpisodes(featured || []);
      setYCEpisodes(yc || []);
      setTopVCEpisodes(topvc || []);
    } catch (error) {
      console.error('Error refreshing tags:', error);
    }
  };

  const handleUnfeature = async (episodeGuid) => {
    const featured = featuredEpisodes.find(f => f.episode_guid === episodeGuid);
    if (!featured) return;
    
    try {
      await removeFeaturedEpisode(featured._id);
      // Only refresh tags, not episodes - preserves scroll position
      setFeaturedEpisodes(prev => prev.filter(f => f.episode_guid !== episodeGuid));
    } catch (error) {
      console.error('Error unfeaturing episode:', error);
      alert('Error unfeaturing episode');
    }
  };

  const handleTagYC = async (episode) => {
    try {
      const newYC = await addYCEpisode({
        episode_guid: episode.guid,
        title: episode.title,
        active: true
      });
      // Update state directly instead of reloading - preserves scroll position
      setYCEpisodes(prev => [...prev, newYC]);
    } catch (error) {
      console.error('Error tagging YC episode:', error);
      alert('Error tagging as YC interview');
    }
  };

  const handleUntagYC = async (episodeGuid) => {
    const yc = ycEpisodes.find(y => y.episode_guid === episodeGuid);
    if (!yc) return;
    
    try {
      await removeYCEpisode(yc._id);
      // Update state directly - preserves scroll position
      setYCEpisodes(prev => prev.filter(y => y.episode_guid !== episodeGuid));
    } catch (error) {
      console.error('Error untagging YC episode:', error);
      alert('Error removing YC tag');
    }
  };

  const handleTagTopVC = async (episode) => {
    try {
      const newTopVC = await addTopVCEpisode({
        episode_guid: episode.guid,
        title: episode.title,
        active: true
      });
      // Update state directly - preserves scroll position
      setTopVCEpisodes(prev => [...prev, newTopVC]);
    } catch (error) {
      console.error('Error tagging Top VC episode:', error);
      alert('Error tagging as Sequoia/a16z interview');
    }
  };

  const handleUntagTopVC = async (episodeGuid) => {
    const topvc = topvcEpisodes.find(t => t.episode_guid === episodeGuid);
    if (!topvc) return;
    
    try {
      await removeTopVCEpisode(topvc._id);
      // Update state directly - preserves scroll position
      setTopVCEpisodes(prev => prev.filter(t => t.episode_guid !== episodeGuid));
    } catch (error) {
      console.error('Error untagging Top VC episode:', error);
      alert('Error removing Sequoia/a16z tag');
    }
  };

  const isFeatured = (guid) => {
    return featuredEpisodes.some(f => f.episode_guid === guid);
  };

  const isYC = (guid) => {
    return ycEpisodes.some(y => y.episode_guid === guid);
  };

  const isTopVC = (guid) => {
    return topvcEpisodes.some(t => t.episode_guid === guid);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <AdminLayout currentPage="podcast">
        <div className="text-center py-20">
          <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-400">Loading episodes...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout currentPage="podcast">
      {/* Stats */}
      <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold text-white">Podcast Episodes</h2>
          <p className="text-slate-400 text-sm">Manage featured episodes, YC founders, and Sequoia/a16z interviews</p>
        </div>
        <div className="flex gap-2">
          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
            <Star className="w-3 h-3 mr-1" />
            {featuredEpisodes.length} Featured
          </Badge>
          <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">
            <span className="font-bold mr-1">Y</span>
            {ycEpisodes.length} YC Interviews
          </Badge>
          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
            <span className="font-bold mr-1">VC</span>
            {topvcEpisodes.length} Sequoia/a16z
          </Badge>
        </div>
      </div>

      {/* Top VC Episodes Section */}
      {topvcEpisodes.length > 0 && (
        <Card className="mb-6 bg-slate-900 border-green-500/30">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <span className="w-6 h-6 bg-green-600 rounded flex items-center justify-center text-white text-xs font-bold">VC</span>
              Sequoia / a16z Founder Interviews
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {topvcEpisodes.map((topvc) => {
                const episode = episodes.find(e => e.guid === topvc.episode_guid);
                return (
                  <div key={topvc._id} className="flex items-center justify-between bg-slate-800/50 p-3 rounded-lg">
                    <div className="flex-1 min-w-0 mr-4">
                      <p className="text-white font-medium truncate">{topvc.title}</p>
                      {episode && (
                        <p className="text-slate-400 text-sm">{formatDate(episode.pubDate)}</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleUntagTopVC(topvc.episode_guid)}
                      className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                    >
                      Remove Tag
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* YC Episodes Section */}
      {ycEpisodes.length > 0 && (
        <Card className="mb-6 bg-slate-900 border-orange-500/30">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <span className="w-6 h-6 bg-orange-500 rounded flex items-center justify-center text-white text-sm font-bold">Y</span>
              YC Founder Interviews
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {ycEpisodes.map((yc) => {
                const episode = episodes.find(e => e.guid === yc.episode_guid);
                return (
                  <div key={yc._id} className="flex items-center justify-between bg-slate-800/50 p-3 rounded-lg">
                    <div className="flex-1 min-w-0 mr-4">
                      <p className="text-white font-medium truncate">{yc.title}</p>
                      {episode && (
                        <p className="text-slate-400 text-sm">{formatDate(episode.pubDate)}</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleUntagYC(yc.episode_guid)}
                      className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                    >
                      Remove YC Tag
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Featured Section */}
      {featuredEpisodes.length > 0 && (
        <Card className="mb-6 bg-slate-900 border-yellow-500/30">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-400" />
              Featured Episodes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {featuredEpisodes.map((featured) => {
                const episode = episodes.find(e => e.guid === featured.episode_guid);
                return (
                  <div key={featured._id} className="flex items-center justify-between bg-slate-800/50 p-3 rounded-lg">
                    <div className="flex-1 min-w-0 mr-4">
                      <p className="text-white font-medium truncate">{featured.title}</p>
                      {episode && (
                        <p className="text-slate-400 text-sm">{formatDate(episode.pubDate)}</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleUnfeature(featured.episode_guid)}
                      className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                    >
                      <StarOff className="w-4 h-4 mr-1" />
                      Unfeature
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Episodes */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">
          All Episodes 
          <span className="text-slate-400 text-sm font-normal ml-2">
            (showing {episodes.length} of {totalEpisodes})
          </span>
        </h3>
        {episodes.length < totalEpisodes && (
          <Button
            onClick={loadAllEpisodes}
            disabled={loadingMore}
            variant="outline"
            className="border-slate-600 text-slate-300"
          >
            {loadingMore ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              `Load All ${totalEpisodes} Episodes`
            )}
          </Button>
        )}
      </div>
      <div className="space-y-3">
        {episodes.map((episode) => {
          const featured = isFeatured(episode.guid);
          const yc = isYC(episode.guid);
          const topvc = isTopVC(episode.guid);
          return (
            <Card 
              key={episode.guid} 
              className={`bg-slate-900 ${featured ? 'border-yellow-500/30' : yc ? 'border-orange-500/30' : topvc ? 'border-green-500/30' : 'border-slate-800'}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Feature Button */}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => featured ? handleUnfeature(episode.guid) : handleFeature(episode)}
                    className={`flex-shrink-0 ${featured ? 'text-yellow-400 hover:text-yellow-300' : 'text-slate-500 hover:text-yellow-400'}`}
                    title={featured ? 'Remove from featured' : 'Add to featured'}
                  >
                    <Star className={`w-5 h-5 ${featured ? 'fill-current' : ''}`} />
                  </Button>
                  
                  {/* YC Button */}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => yc ? handleUntagYC(episode.guid) : handleTagYC(episode)}
                    className={`flex-shrink-0 ${yc ? 'text-orange-400 hover:text-orange-300 bg-orange-500/20' : 'text-slate-500 hover:text-orange-400'}`}
                    title={yc ? 'Remove YC tag' : 'Tag as YC interview'}
                  >
                    <span className="font-bold text-sm">Y</span>
                  </Button>
                  
                  {/* Top VC Button */}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => topvc ? handleUntagTopVC(episode.guid) : handleTagTopVC(episode)}
                    className={`flex-shrink-0 ${topvc ? 'text-green-400 hover:text-green-300 bg-green-500/20' : 'text-slate-500 hover:text-green-400'}`}
                    title={topvc ? 'Remove Sequoia/a16z tag' : 'Tag as Sequoia/a16z interview'}
                  >
                    <span className="font-bold text-xs">VC</span>
                  </Button>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2 mb-1 flex-wrap">
                      {featured && (
                        <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-xs flex-shrink-0">
                          Featured
                        </Badge>
                      )}
                      {yc && (
                        <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-xs flex-shrink-0">
                          YC Founder
                        </Badge>
                      )}
                      {topvc && (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs flex-shrink-0">
                          Sequoia/a16z
                        </Badge>
                      )}
                      {episode.episode && (
                        <Badge variant="outline" className="text-xs border-slate-700 text-slate-400 flex-shrink-0">
                          #{episode.episode}
                        </Badge>
                      )}
                    </div>
                    <h3 className="text-white font-medium mb-1 line-clamp-2">
                      {episode.title}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-slate-400">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {episode.duration}
                      </span>
                      <span>{formatDate(episode.pubDate)}</span>
                    </div>
                  </div>
                  
                  <a
                    href={episode.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0 text-slate-400 hover:text-white"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </AdminLayout>
  );
};

export default AdminPodcast;
